// Shared helpers for computing the final destination URL a shopper lands
// on after clicking /r/<slug>?p=<product_id>. Used by:
//   - PublicLink.jsx → actually performs the redirect
//   - SharePage.jsx → shows the affiliate a live preview of the URL so
//     they can sanity-check before posting to WhatsApp / Facebook.
//
// Keep all URL-mangling logic here so the live preview can never drift
// out of sync with the real redirect.

export function appendQueryParam(url, key, value) {
  if (!url || !value) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}${key}=${encodeURIComponent(value)}`;
}

export function isWhatsAppUrl(url) {
  if (!url) return false;
  try {
    const h = new URL(url).host.toLowerCase();
    return h === "wa.me" || h === "api.whatsapp.com" || h === "chat.whatsapp.com";
  } catch {
    return false;
  }
}

// When ?p=<productId> is present, that param is authoritative. Rewrite
// the last path segment under /products|/product|/p to the new productId
// so a single affiliate link can serve every product on a host (the link
// record is reused across products by /api/share/intent).
export function overrideProductPath(url, productId) {
  if (!url || !productId) return url;
  try {
    const u = new URL(url);
    const m = u.pathname.match(/^(.*\/(?:products?|p)\/)([^/]+)(\/?.*)$/i);
    if (m) {
      u.pathname = m[1] + encodeURIComponent(productId) + m[3];
      return u.toString();
    }
    return url;
  } catch {
    return url;
  }
}

// Belt-and-suspenders: ensure ?ref=<slug> is on every non-WhatsApp URL.
// The backend already does this, but we re-do it client-side so the
// affiliate's link is never naked even if the backend response is stale.
export function ensureRef(url, slug) {
  if (!url || !slug) return url;
  if (isWhatsAppUrl(url)) return url;
  if (/[?&]ref=/i.test(url)) return url;
  return appendQueryParam(url, "ref", slug);
}

// Single source of truth for "what URL does the shopper actually land on?"
//   finalUrl = backend's final_destination_url (already has ?ref)
//   slug     = affiliate slug (safety net if ?ref missing)
//   productId = the ?p=<id> param the shopper's tracking URL carried
export function computeLandingUrl({ finalUrl, slug, productId }) {
  if (!finalUrl) return null;
  let target = ensureRef(finalUrl, slug);
  if (productId) {
    target = overrideProductPath(target, productId);
    target = appendQueryParam(target, "p", productId);
  }
  return target;
}
